import type { TPractice } from "@/api/models/practice/practice";
import type { TComposableParams } from "@/composables/shared/filters/useGeneralFilter";
import type { TItemQueryComposableParams } from "@/composables/shared/filters/useGeneralFilter";
import { splitComposableParams } from "@/composables/shared/filters/useGeneralFilter";
import type { TSelectFieldDynamicProps } from "@/types/selectField";
import PracticeMeService, {
  normalizePracticeMeListResponse,
  type IPracticeMeFilters,
} from "@/api/models/practice/practiceMe";
import PracticeService, { type IPracticeFilters } from "@/api/models/practice/practice";
import type { DefaultPaginatedFilters, PaginatedResponse } from "@/api/shared/filters";
import {
  useGenericListQuery,
  useGenericPaginatedQuery,
  useGenericQuery,
  createQueryKeys,
  type TItemQueryBehaviorOptions,
  type TOwnedFilterParams,
} from "@/composables/shared/queries";
import {
  createPathParamCrudMutations,
  type IPathParamCrudService,
} from "@/composables/shared/mutations/useCrudMutations";
import type { TMutationComposableParams } from "@/composables/shared/mutations/useGenericMutation";
import { PracticeQueryKeys } from "@/composables/models/practice/usePractice";
import { ESubjects } from "@/api/models/auth/ability";
import type { MaybeRefOrGetter } from "vue";
import { computed, toRef, toValue, watch } from "vue";
import type { AxiosResponse } from "axios";

export type TUsePracticeDetailReturn = ReturnType<typeof useGenericQuery<TPractice, string>>;

export const PracticeMeQueryKeys = createQueryKeys(PracticeMeService.queryKey, (key) => ({
  detail: key<string>("detail"),
  paginated: key<IPracticeMeFilters>("paginated"),
  byCompany: key<IPracticeMeFilters>("by-company"),
}));

const { useUpdate: useUpdatePracticeMeMutation } = createPathParamCrudMutations<
  TPractice,
  { id?: string }
>(PracticeMeService as unknown as IPathParamCrudService<TPractice, { id?: string }>, [], {
  entityName: "practice-me",
  permissionSubject: ESubjects.PRACTICE__PRACTICE,
  invalidates: [
    PracticeMeQueryKeys.all(),
    PracticeMeQueryKeys.paginated(),
    PracticeMeQueryKeys.byCompany(),
    PracticeQueryKeys.detail(),
    PracticeQueryKeys.paginated(),
    PracticeQueryKeys.all(),
  ],
});

export const usePracticeMePaginated = (params: TOwnedFilterParams<IPracticeMeFilters> = {}) => {
  return useGenericPaginatedQuery<TPractice, Partial<IPracticeMeFilters>>({
    queryKey: PracticeMeQueryKeys.paginated(),
    params,
    fetcher: async ({ filters: f, pageParam }) =>
      PracticeMeService.getPaginatedFiltered({}, f ?? {}, pageParam),
    permission: $permSubjects.PRACTICE__PRACTICE,
    action: $permActions.PRACTICE_ME_VIEW,
    noRetryStatusCodes: [404, 412],
  });
};

export const usePracticeMeList = (
  filters: MaybeRefOrGetter<Partial<IPracticeMeFilters>>,
  enabled: MaybeRefOrGetter<boolean> = true,
  options: TItemQueryBehaviorOptions = {}
) => {
  return useGenericListQuery<TPractice, Partial<IPracticeMeFilters>>({
    queryKey: PracticeMeQueryKeys.all(),
    params: { filters, enabled },
    fetcher: async (f) => {
      const response = await PracticeMeService.getAll({}, f);
      const items = normalizePracticeMeListResponse(
        response.data as TPractice[] | PaginatedResponse<TPractice>
      );
      return { data: items } as AxiosResponse<TPractice[]>;
    },
    permission: $permSubjects.PRACTICE__PRACTICE,
    action: $permActions.PRACTICE_ME_VIEW,
    ...options,
  });
};

export const usePracticesByCompany = (
  companyId: MaybeRefOrGetter<string | undefined>,
  options: {
    useMe?: MaybeRefOrGetter<boolean>;
    enabled?: MaybeRefOrGetter<boolean>;
  } = {}
) => {
  const { useMe = false, enabled = true } = options;
  const useMeRef = computed(() => toValue(useMe));

  const filters = computed<Partial<IPracticeMeFilters & IPracticeFilters>>(() => ({
    company: toValue(companyId),
  }));

  const queryKey = computed(() =>
    useMeRef.value ? PracticeMeQueryKeys.byCompany() : PracticeQueryKeys.byCompany()
  );

  const query = useGenericListQuery<TPractice, Partial<IPracticeMeFilters & IPracticeFilters>>({
    queryKey,
    params: {
      filters,
      enabled: computed(() => toValue(enabled) && Boolean(toValue(companyId))),
    },
    fetcher: async (f) => {
      const companyFilter = f?.company
        ? ({ company: f.company } as Partial<DefaultPaginatedFilters>)
        : undefined;
      if (useMeRef.value) {
        const response = await PracticeMeService.getAll({}, companyFilter);
        const items = normalizePracticeMeListResponse(
          response.data as TPractice[] | PaginatedResponse<TPractice>
        );
        return { data: items } as AxiosResponse<TPractice[]>;
      }
      return PracticeService.getAll({}, companyFilter);
    },
  });

  return {
    ...query,
    practices: computed(() => query.data.value ?? []),
  };
};

export const usePracticeDetail = (
  params: MaybeRefOrGetter<TItemQueryComposableParams<string>>,
  options: { useMe?: MaybeRefOrGetter<boolean> } & TItemQueryBehaviorOptions = {}
): TUsePracticeDetailReturn => {
  const { useMe = false, ...queryBehaviorOptions } = options;
  const useMeRef = computed(() => toValue(useMe));
  const resolvedParams = computed(() => toValue(params));
  const idRef = computed(() => toValue(resolvedParams.value.id));

  const queryKey = computed(() =>
    useMeRef.value ? PracticeMeQueryKeys.detail() : PracticeQueryKeys.detail()
  );

  return useGenericQuery<TPractice, string>({
    queryKey,
    params: () => ({
      id: idRef.value,
      enabled: (resolvedParams.value.enabled ?? true) && !!idRef.value,
    }),
    fetcher: async (id) => {
      if (useMeRef.value) {
        return PracticeMeService.get({ id });
      }
      return PracticeService.get({ id });
    },
    ...queryBehaviorOptions,
    options: {
      refetchOnMount: true,
    },
  });
};

export const usePracticeMeForSelect = (
  extraParams?: MaybeRefOrGetter<Partial<IPracticeMeFilters> & TComposableParams>
): TSelectFieldDynamicProps<TPractice, IPracticeMeFilters> => {
  const refExtraParams = toRef(extraParams);
  const { filters: initialFilters } = splitComposableParams<IPracticeMeFilters>(
    toValue(refExtraParams)
  );

  // Start disabled so the first HTTP request is the page_size=1 single-item check,
  // not a page_size=20 call that fires and is abandoned before the observer settles.
  // useSelectOptionItems enables this ref atomically when it sets page_size=1.
  // Deliberately a literal `false`, not the caller's `enabled` — the gate is owned by the
  // select, and `useOwnedFilterRefs` keeps a plain boolean writable for it.
  const query = usePracticeMePaginated({
    initial: initialFilters,
    enabled: false,
  });

  watch(
    refExtraParams,
    (val: (Partial<IPracticeMeFilters> & TComposableParams) | undefined) => {
      const { filters: nextFilters } = splitComposableParams<IPracticeMeFilters>(val);
      query.filters.value = { ...query.filters.value, ...nextFilters };
    },
    { deep: true }
  );

  return {
    paginatedQuery: query,
    queryFilters: query.filters,
    filterField: "query",
    queryEnabled: query.queryEnabled,
  };
};

export const useUpdatePracticeMe = (params?: TMutationComposableParams<Partial<TPractice>>) =>
  useUpdatePracticeMeMutation({
    permissions: {
      [$permActions.PRACTICE_ME_EDIT]: $permSubjects.PRACTICE__PRACTICE,
    },
    ...params,
  });
